import type { DesignSpec } from "@/types/designSpec";
import type { RenderedDesignSpec } from "@/types/generatedImage";
import {
  createDesignSpecSnapshot,
  parseDesignSpecSnapshot,
} from "@/lib/agent/designSpecSnapshot";

const BACK_FIELDS = ["backPrompt", "backStyle", "closure"] as const;
const isBackReference = (reference: DesignSpec["references"][number]) =>
  reference.side === "back" || reference.role === "back";

/** The contracts belonging to the visible generated images, never the draft. */
export function getGeneratedDesignSpec(
  images: Record<
    "front" | "back",
    { url: string | null; designSpec: RenderedDesignSpec }
  >,
  { preserveUnrenderedBack = false } = {}
): RenderedDesignSpec {
  const front = parseDesignSpecSnapshot(images.front.designSpec);
  const back = parseDesignSpecSnapshot(images.back.designSpec);
  if (!images.front.url || !front || (images.back.url && !back)) return null;

  const saved: DesignSpec = {
    ...front,
    visual: { ...front.visual },
    provenance: { ...front.provenance },
  };
  if (images.back.url && back) {
    // A back may be saved after the front. Its back-only decisions belong to
    // the visible pair; shared front decisions still come from the front.
    for (const field of BACK_FIELDS) {
      saved.visual[field] = back.visual[field];
      const path = `visual.${field}`;
      delete saved.provenance[path];
      if (back.provenance[path]) saved.provenance[path] = back.provenance[path];
    }
    if (JSON.stringify(front.references) !== JSON.stringify(back.references)) {
      saved.references = [
        ...front.references.filter(reference => !isBackReference(reference)),
        ...back.references.filter(isBackReference),
      ];
    }
  } else if (!preserveUnrenderedBack) {
    // Back ideas can already exist when the front is rendered. A quote for
    // the saved front must not promise a back that has never been previewed.
    for (const field of BACK_FIELDS) {
      delete saved.visual[field];
      delete saved.provenance[`visual.${field}`];
    }
    saved.references = saved.references.filter(
      reference => !isBackReference(reference)
    );
  }
  return createDesignSpecSnapshot(saved);
}

/** The saved front contract alone, even when a back render also exists. */
export function getGeneratedFrontDesignSpec(front: {
  url: string | null;
  designSpec: RenderedDesignSpec;
}): RenderedDesignSpec {
  return getGeneratedDesignSpec({
    front,
    back: { url: null, designSpec: null },
  });
}

/**
 * Refreshing an older Back must use the Front that is currently on screen.
 * Carry a newly requested Back direction forward without letting an old chat
 * capability overwrite the saved Front's shared design fields.
 */
export function getBackDesignSpecForLatestFront(
  images: Parameters<typeof getGeneratedDesignSpec>[0],
  requested: DesignSpec | null | undefined,
  modificationPrompt?: string
): RenderedDesignSpec {
  // Older sessions can have a Back image without a render contract. Its
  // choices come from the requested draft, while the verified shared design
  // still comes from the saved Front.
  const saved =
    getGeneratedDesignSpec(images) ?? getGeneratedFrontDesignSpec(images.front);
  if (!saved) return null;
  if (!requested && !modificationPrompt?.trim()) return saved;

  requested ??= saved;

  const merged: DesignSpec = {
    ...saved,
    visual: { ...saved.visual },
    provenance: { ...saved.provenance },
    references: [
      ...saved.references.filter(reference => !isBackReference(reference)),
      ...requested.references.filter(isBackReference),
    ],
  };
  for (const field of BACK_FIELDS) {
    const path = `visual.${field}`;
    if (requested.visual[field] === undefined) delete merged.visual[field];
    else merged.visual[field] = requested.visual[field];
    delete merged.provenance[path];
    if (requested.provenance[path])
      merged.provenance[path] = requested.provenance[path];
  }
  const instruction = modificationPrompt?.trim();
  if (instruction) {
    const previous =
      merged.visual.backPrompt?.trim() || "Back of the current front dress";
    // A signed modify action may carry only the customer's edit instruction.
    // Keep it in the Back render contract as well as the generation prompt so
    // a paid refresh cannot silently recreate the unchanged older Back.
    merged.visual.backPrompt = previous
      .toLowerCase()
      .includes(instruction.toLowerCase())
      ? previous
      : `${previous.slice(0, 4500)}\nRequested Back change: ${instruction.slice(0, 4000)}. Follow the new request where it conflicts with older Back details.`;
    merged.provenance["visual.backPrompt"] = {
      source: "explicit_user",
      confirmed: true,
    };
  }
  return createDesignSpecSnapshot(merged);
}
